import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { 
  CreditCard, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Calendar,
  DollarSign,
  Users,
  Zap,
  Crown,
  Star,
  Clock,
  Monitor
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { toast } from 'react-hot-toast'
import SubscriptionService from '@/utils/services/SubscriptionService'
import userService from '@/utils/services/UserService'
import Config from '@/utils/Config'
import { getDeviceLimit, getScreenLimit } from '@/utils/subscriptionUtils'

const Subscriptions = () => {
  const { currentUser, currentUserData } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [subscriptions, setSubscriptions] = useState([])
  const [userSubscriptions, setUserSubscriptions] = useState([])
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(true)
  const [upgradeLoading, setUpgradeLoading] = useState(false)
  const [trialLoading, setTrialLoading] = useState(false)
  const [showUpgradeConfirm, setShowUpgradeConfirm] = useState(false)
  const [showTrialConfirm, setShowTrialConfirm] = useState(false)
  const [showCancelTrialConfirm, setShowCancelTrialConfirm] = useState(false)
  const [showPurchaseSuccess, setShowPurchaseSuccess] = useState(false)
  const [showPurchaseCanceled, setShowPurchaseCanceled] = useState(false)
  const [selectedSubscription, setSelectedSubscription] = useState(null)

  const loadSubscriptions = async () => {
    try {
      // Load subscriptions using the service with caching
      const sortedSubscriptions = await SubscriptionService.loadSubscriptions(true)
      
      // Get user subscriptions using the new UserService function
      const userSubs = await userService.getUserSubscriptions(currentUser.uid, true)
      
      // Debug: Log subscription data structure
      console.log('🔍 User Subscriptions Data:', userSubs)
      if (userSubs.length > 0) {
        console.log('🔍 First Subscription Structure:', userSubs[0])
        console.log('🔍 Items Data:', userSubs[0].items)
        console.log('🔍 Price Data:', userSubs[0].price)
        console.log('🔍 Amount Data:', userSubs[0].amount)
      }
      
      // Load user devices using the service
      const userDevices = await SubscriptionService.loadUserDevices(currentUser.uid)
      
      setSubscriptions(sortedSubscriptions)
      setUserSubscriptions(userSubs)
      setDevices(userDevices)
      setLoading(false)
    } catch (error) {
      console.error('Error loading subscriptions:', error)
      toast.error('Failed to load subscription data')
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSubscriptions()
  }, [currentUserData, currentUser.uid])

  // Keep user subscriptions live - the Stripe billing portal opens in another
  // tab, and cancels/changes made there arrive later via the Stripe webhook
  useEffect(() => {
    let unsubscribe = null
    let active = true

    userService.listenToUserSubscriptions(currentUser.uid, (userSubs) => {
      if (active) {
        setUserSubscriptions(userSubs)
      }
    })
      .then((unsub) => {
        if (active) {
          unsubscribe = unsub
        } else {
          unsub()
        }
      })
      .catch((error) => console.error('Error subscribing to user subscriptions:', error))

    return () => {
      active = false
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [currentUser.uid])

  // Handle URL parameters for purchase success/canceled
  useEffect(() => {
    const purchaseSuccess = searchParams.get('purchase_success')
    const purchaseCanceled = searchParams.get('purchase_canceled')

    if (purchaseSuccess === 'true') {
      // Show success modal and refresh data first
      setShowPurchaseSuccess(true)
      // Remove the parameter from URL
      searchParams.delete('purchase_success')
      setSearchParams(searchParams)
      
      // Refresh subscription data and then end trial if needed
      const refreshAndEndTrial = async () => {
        try {
          // Refresh subscription data first
          await loadSubscriptions()
          
          // Wait a bit for the data to be updated
          setTimeout(async () => {
            try {
              // Get fresh subscription data directly from the service
              const freshUserSubs = await userService.getUserSubscriptions(currentUser.uid, false) // Don't use cache
              const freshSubscriptions = await SubscriptionService.loadSubscriptions(false) // Don't use cache
              
              // Check if user has active paid subscription after refresh
              const hasPaidSub = freshUserSubs.some(sub => 
                sub.status === 'active' && 
                !freshSubscriptions.find(s => s.key === sub.product?.id)?.isTrial
              )
              
              if (hasPaidSub && currentUser) {
                await SubscriptionService.endTrial(currentUser.uid)
                console.log('Trial ended for user after successful purchase:', currentUser.uid)
                // Refresh data again after ending trial
                await loadSubscriptions()
              }
            } catch (error) {
              console.error('Error ending trial after successful purchase:', error)
            }
          }, 1000) // Wait 1 second for data to refresh
        } catch (error) {
          console.error('Error refreshing subscription data:', error)
        }
      }
      
      refreshAndEndTrial()
    }

    if (purchaseCanceled === 'true') {
      // Show purchase canceled modal
      setShowPurchaseCanceled(true)
      // Remove the parameter from URL
      searchParams.delete('purchase_canceled')
      setSearchParams(searchParams)
    }
  }, [searchParams, setSearchParams, userSubscriptions, subscriptions, currentUser])

  // Prevent body scrolling when modals are open
  useEffect(() => {
    if (showUpgradeConfirm || showTrialConfirm || showCancelTrialConfirm || showPurchaseSuccess || showPurchaseCanceled) {
      const originalOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      
      return () => {
        document.body.style.overflow = originalOverflow
      }
    }
  }, [showUpgradeConfirm, showTrialConfirm, showCancelTrialConfirm, showPurchaseSuccess, showPurchaseCanceled])

  const getStatusIcon = (subscription) => {
    const statusInfo = SubscriptionService.getStatusInfo(subscription, currentUserData, userSubscriptions, subscriptions)
    
    switch (statusInfo.icon) {
      case 'CheckCircle':
        return subscription.isTrial && statusInfo.isCurrent 
          ? <CheckCircle className="w-5 h-5 text-blue-500" />
          : <CheckCircle className="w-5 h-5 text-green-500" />
      case 'AlertCircle':
        return <AlertCircle className="w-5 h-5 text-blue-500" />
      case 'XCircle':
        return <XCircle className="w-5 h-5 text-gray-400" />
      default:
        return <XCircle className="w-5 h-5 text-gray-400" />
    }
  }

  const getStatusColor = (subscription) => {
    const statusInfo = SubscriptionService.getStatusInfo(subscription, currentUserData, userSubscriptions, subscriptions)
    return statusInfo.color
  }

  const getStatusText = (subscription) => {
    const statusInfo = SubscriptionService.getStatusInfo(subscription, currentUserData, userSubscriptions, subscriptions)
    return statusInfo.text
  }

  const isCurrentPlan = (subscription) => {
    return SubscriptionService.isCurrentPlan(subscription, currentUserData, userSubscriptions, subscriptions)
  }

  const hasUsedTrial = () => {
    return SubscriptionService.hasUsedTrial(currentUserData)
  }

  const canStartTrial = (subscription) => {
    return SubscriptionService.canStartTrial(currentUserData, subscription)
  }

  const hasActivePaidSubscription = () => {
    return userSubscriptions.some(sub => 
      sub.status === 'active' && 
      !subscriptions.find(s => s.key === sub.product?.id)?.isTrial
    )
  }

  const shouldGrayOutTrial = (subscription) => {
    return subscription.isTrial && hasActivePaidSubscription()
  }

  const getCurrentPlanName = () => {
    // Check if user has admin privileges first
    if (currentUserData?.isAdmin) {
      return 'Admin Plan'
    }
    
    // Use SubscriptionService to get current plan name
    return SubscriptionService.getCurrentPlanName(currentUserData, userSubscriptions, subscriptions)
  }

  const getCurrentPlanIcon = () => {
    // Check if user has admin privileges first
    if (currentUserData?.isAdmin) {
      return <Crown className="w-4 h-4 text-yellow-600" />
    }
    
    // Check if user has active paid subscription first (prioritize over trial)
    if (userSubscriptions.length > 0) {
      const mostExpensive = SubscriptionService.getMostExpensiveActiveSubscription(userSubscriptions, subscriptions)
      if (mostExpensive) {
        if (mostExpensive.plan.metadata?.devices === 'unlimited' || 
            parseInt(mostExpensive.plan.metadata?.devices) > 100) {
          return <Crown className="w-4 h-4 text-yellow-600" />
        }
        return <Star className="w-4 h-4 text-primary-600" />
      }
    }
    
    // Check if user is in trial (only if no active paid subscription)
    if (currentUserData?.trialData && 
        currentUserData.trialData.endDate > new Date().getTime() && 
        !currentUserData.trialData.expired) {
      // Check if trial is canceled
      if (currentUserData.trialData.cancelled) {
        return <XCircle className="w-4 h-4 text-orange-600" />
      }
      return <AlertCircle className="w-4 h-4 text-blue-600" />
    }
    
    // Default to free plan (only if no admin, no paid subscription, and no trial)
    return <Users className="w-4 h-4 text-gray-600" />
  }

  // Function to identify the "best choice" package (typically middle-tier with good value)
  const getBestChoicePackage = () => {
    if (subscriptions.length === 0) return null;
    
    // Look specifically for a package with "Silver" in the name
    const silverPackage = subscriptions.find(sub => 
      sub.name && sub.name.toLowerCase().includes('silver')
    );
    
    if (silverPackage) return silverPackage;
    
    // Fallback: if no Silver package found, look for packages with "silver" in the key
    const silverByKey = subscriptions.find(sub => 
      sub.key && sub.key.toLowerCase().includes('silver')
    );
    
    if (silverByKey) return silverByKey;
    
    // If still no Silver package, return null (no best choice highlighting)
    return null;
  }

  const getPrice = (subscription) => {
    return SubscriptionService.getFormattedPrice(subscription)
  }

  const getInterval = (subscription) => {
    return SubscriptionService.getInterval(subscription)
  }

  const getFeatures = (subscription) => {
    return SubscriptionService.getFeatures(subscription)
  }

  const handleUpgrade = (subscription) => {
    setSelectedSubscription(subscription)
    if (subscription.isTrial) {
      if (canStartTrial(subscription)) {
        setShowTrialConfirm(true)
      } else {
        toast.error('You have already used your free trial')
      }
    } else {
      setShowUpgradeConfirm(true)
    }
  }

  const handleCancel = (planId) => {
    // Find the subscription to check if it's a trial
    const subscription = subscriptions.find(sub => sub.key === planId)
    
    if (subscription?.isTrial) {
      // Show trial cancellation confirmation modal
      setSelectedSubscription(subscription)
      setShowCancelTrialConfirm(true)
    } else {
      // For paid subscriptions, open Stripe customer portal
      window.open(Config.stripeSubscriptionsPage, '_blank')
    }
  }

  const confirmUpgrade = async () => {
    if (!selectedSubscription || !currentUser) return

    setUpgradeLoading(true)

    try {
      // Get the price key from the selected subscription
      const priceKey = selectedSubscription.prices[0]?.key
      if (!priceKey) {
        toast.error('No price found for this subscription')
        setShowUpgradeConfirm(false)
        setSelectedSubscription(null)
        setUpgradeLoading(false)
        return
      }

      // Use the new subscribe method
      await SubscriptionService.subscribe(
        currentUser.uid,
        priceKey,
        window.location.href + "?purchase_success=true",
        window.location.href + "?purchase_canceled=true",
        (success, result) => {
          if (success) {
            // Redirect to Stripe checkout
            window.location.href = result
          } else {
            console.error('Checkout error:', result)
            toast.error('Failed to create checkout session')
            setShowUpgradeConfirm(false)
            setSelectedSubscription(null)
            setUpgradeLoading(false)
          }
        }
      )

    } catch (error) {
      console.error('Subscription error:', error)
      toast.error('Failed to start subscription')
      setShowUpgradeConfirm(false)
      setSelectedSubscription(null)
      setUpgradeLoading(false)
    }
  }

  const confirmTrial = async () => {
    if (!selectedSubscription || !currentUser) return

    setTrialLoading(true)

    try {
      // Start trial using the service
      await SubscriptionService.startTrial(currentUser.uid, selectedSubscription.metadata)
      toast.success('Trial started successfully!')
      setShowTrialConfirm(false)
      setSelectedSubscription(null)
      // Reload the page to reflect the new trial status
      window.location.reload()
    } catch (error) {
      console.error('Trial error:', error)
      toast.error('Failed to start trial')
      setShowTrialConfirm(false)
      setSelectedSubscription(null)
      setTrialLoading(false)
    }
  }

  const confirmCancelTrial = async () => {
    if (!currentUser) return

    setTrialLoading(true)

    try {
      // Cancel trial using the service
      await SubscriptionService.cancelTrial(currentUser.uid)
      toast.success('Trial cancelled successfully!')
      setShowCancelTrialConfirm(false)
      setSelectedSubscription(null)
      // Reload the page to reflect the new trial status
      window.location.reload()
    } catch (error) {
      console.error('Trial cancellation error:', error)
      toast.error('Failed to cancel trial')
      setShowCancelTrialConfirm(false)
      setSelectedSubscription(null)
    } finally {
      setTrialLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscriptions</h1>
          <p className="text-gray-600">Manage your subscription plans and billing</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1 bg-primary-50 text-primary-700 rounded-full">
            {getCurrentPlanIcon()}
            <span className="text-sm font-medium">
              {getCurrentPlanName()}
            </span>
          </div>
        </div>
      </div>

      {/* Current Plan Summary */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary-100 rounded-lg">
            <CreditCard className="w-5 h-5 text-primary-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Current Plan</h2>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <DollarSign className="w-5 h-5 text-gray-500" />
            <div>
              <p className="text-sm text-gray-600">
                {currentUserData?.trialData && 
                 currentUserData.trialData.endDate > new Date().getTime() && 
                 !currentUserData.trialData.expired ? 'Trial Cost' : 'Monthly Cost'}
              </p>
              <p className="text-lg font-semibold text-gray-900">
                {currentUserData?.trialData && 
                 currentUserData.trialData.endDate > new Date().getTime() && 
                 !currentUserData.trialData.expired
                  ? 'Free'
                  : userSubscriptions.length > 0 && subscriptions.length > 0 
                                      ? (() => {
                                          const mostExpensive = SubscriptionService.getMostExpensiveActiveSubscription(userSubscriptions, subscriptions);
                                          if (mostExpensive) {
                                            return `C$${getPrice(mostExpensive.plan)}`;
                                          }
                                          return `C$${getPrice(subscriptions.find(s => s.key === userSubscriptions[0].product.id) || subscriptions[0])}`;
                                        })()
                  : 'C$0.00'
                }
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <Calendar className="w-5 h-5 text-gray-500" />
            <div>
              <p className="text-sm text-gray-600">
                {currentUserData?.trialData && 
                 currentUserData.trialData.endDate > new Date().getTime() && 
                 !currentUserData.trialData.expired ? 'Trial Ends' : 
                 (() => {
                   // Check if user has canceled but valid subscription
                   if (SubscriptionService.isOnlyCanceledButValid(userSubscriptions, subscriptions)) {
                     return 'Access Until';
                   }
                   return 'Next Billing';
                 })()}
              </p>
              <p className="text-lg font-semibold text-gray-900">
                {currentUserData?.trialData && 
                 currentUserData.trialData.endDate > new Date().getTime() && 
                 !currentUserData.trialData.expired
                  ? new Date(currentUserData.trialData.endDate).toLocaleDateString()
                  : userSubscriptions.length > 0 
                    ? (() => {
                        const mostExpensive = SubscriptionService.getMostExpensiveActiveSubscription(userSubscriptions, subscriptions);
                        const source = mostExpensive ? mostExpensive.subscription : userSubscriptions[0];
                        const periodEndMs = SubscriptionService.getPeriodEndMs(source?.current_period_end);

                        if (periodEndMs === null) {
                          return 'N/A';
                        }

                        return new Date(periodEndMs).toLocaleDateString();
                      })()
                    : 'No active subscription'
                }
              </p>
            </div>
          </div>
          
          {/* Info note for canceled subscriptions */}
          {(() => {
            if (SubscriptionService.isOnlyCanceledButValid(userSubscriptions, subscriptions)) {
              return (
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-orange-800 font-medium mb-1">Subscription Canceled</p>
                      <p className="text-sm text-orange-700">
                        Your subscription has been canceled but you still have access to all features until the end of your current billing period.
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })()}
          
          {/* Info note for canceled trials */}
          {currentUserData?.trialData?.cancelled && 
           currentUserData.trialData.endDate > new Date().getTime() && 
           !currentUserData.trialData.expired && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-blue-800 font-medium mb-1">Trial Canceled</p>
                  <p className="text-sm text-blue-700">
                    Your trial has been canceled but you still have access to all trial features until {new Date(currentUserData.trialData.endDate).toLocaleDateString()}.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <Users className="w-5 h-5 text-gray-500" />
            <div>
              <p className="text-sm text-gray-600">Devices</p>
              <p className="text-lg font-semibold text-gray-900">
                {(() => {
                  const deviceLimit = getDeviceLimit(currentUserData, userSubscriptions, subscriptions);
                  const deviceLimitText = deviceLimit === Infinity ? 'Unlimited' : deviceLimit.toString();
                  return `${devices.length} / ${deviceLimitText}`;
                })()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Available Plans */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-green-100 rounded-lg">
            <Star className="w-5 h-5 text-green-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Available Plans</h2>
        </div>
        
        {/* Comparison Table */}
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden shadow-lg ring-1 ring-black ring-opacity-5 rounded-xl">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-4 py-6 text-left text-base font-bold text-gray-900 bg-white border-r border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
                          <Star className="w-4 h-4 text-white" />
                        </div>
                        <span>Features</span>
                      </div>
                    </th>
                    {subscriptions.map((plan) => (
                      <th key={plan.key} className={`px-3 py-6 text-center relative ${
                        isCurrentPlan(plan) 
                          ? 'bg-gradient-to-br from-primary-50 to-primary-100 border-l-2 border-r-2 border-primary-300' 
                          : getBestChoicePackage()?.key === plan.key 
                            ? 'bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 border-l-2 border-r-2 border-amber-400 shadow-lg' 
                            : 'bg-white hover:bg-gray-50 transition-colors'
                      }`}>
                        <div className="space-y-3">
                          {/* Current Plan Badge */}
                          {isCurrentPlan(plan) && (
                            <div className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-full text-xs font-bold shadow-lg">
                              <CheckCircle className="w-3 h-3" />
                              Current
                            </div>
                          )}
                          
                          {/* BEST CHOICE Badge */}
                          {getBestChoicePackage()?.key === plan.key && (
                            <div className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-600 text-white rounded-full text-xs font-bold shadow-lg animate-pulse">
                              <Star className="w-3 h-3" />
                              BEST
                            </div>
                          )}
                          
                          {/* Plan Name */}
                          <div className={`text-lg font-bold ${
                            shouldGrayOutTrial(plan) ? 'text-gray-400' : 'text-gray-900'
                          }`}>
                            {plan.name}
                          </div>
                          
                          {/* Price */}
                          <div className="space-y-1">
                            <div className="flex items-center justify-center gap-1">
                              <span className={`text-2xl font-bold ${
                                shouldGrayOutTrial(plan) ? 'text-gray-400' : 'text-gray-900'
                              }`}>C${getPrice(plan)}</span>
                              <span className={`text-sm ${
                                shouldGrayOutTrial(plan) ? 'text-gray-400' : 'text-gray-500'
                              }`}>/{getInterval(plan)}</span>
                            </div>
                            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              shouldGrayOutTrial(plan) ? 'bg-gray-200 text-gray-500' : getStatusColor(plan)
                            }`}>
                              {getStatusIcon(plan)}
                              {getStatusText(plan)}
                            </div>
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {/* Free Live TV Channels Row */}
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 text-sm font-semibold text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100 border-r border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-green-100 rounded-lg">
                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                        </div>
                        <span>Free Live TV</span>
                      </div>
                    </td>
                    {subscriptions.map((plan) => (
                      <td key={plan.key} className={`px-3 py-4 text-center ${
                        isCurrentPlan(plan) 
                          ? 'bg-gradient-to-r from-primary-50 to-primary-100 border-l-2 border-r-2 border-primary-300' 
                          : getBestChoicePackage()?.key === plan.key 
                            ? 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-100 border-l-2 border-r-2 border-amber-400' 
                            : ''
                      }`}>
                        {plan.isTrial ? (
                          <div className="inline-flex items-center justify-center w-6 h-6 bg-green-100 rounded-full">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          </div>
                        ) : plan.isFree ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                            <CheckCircle className="w-3 h-3" />
                            ✓
                          </span>
                        ) : (
                          <div className="inline-flex items-center justify-center w-6 h-6 bg-green-100 rounded-full">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Premium Live TV Row */}
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 text-sm font-semibold text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100 border-r border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-purple-100 rounded-lg">
                          <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
                        </div>
                        <span>Premium TV</span>
                      </div>
                    </td>
                    {subscriptions.map((plan) => (
                      <td key={plan.key} className={`px-3 py-4 text-center ${
                        isCurrentPlan(plan) 
                          ? 'bg-gradient-to-r from-primary-50 to-primary-100 border-l-2 border-r-2 border-primary-300' 
                          : getBestChoicePackage()?.key === plan.key 
                            ? 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-100 border-l-2 border-r-2 border-amber-400' 
                            : ''
                      }`}>
                        {plan.isTrial ? (
                          <span className="text-gray-400 text-sm">—</span>
                        ) : plan.isFree ? (
                          <span className="text-gray-400 text-sm">—</span>
                        ) : plan.name === 'Single' || plan.name === 'Double' ? (
                          <span className="text-gray-400 text-sm">—</span>
                        ) : (
                          <div className="inline-flex items-center justify-center w-6 h-6 bg-green-100 rounded-full">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Device Limits Row */}
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 text-sm font-semibold text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100 border-r border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-blue-100 rounded-lg">
                          <Users className="w-4 h-4 text-blue-600" />
                        </div>
                        <span>Devices</span>
                      </div>
                    </td>
                    {subscriptions.map((plan) => (
                      <td key={plan.key} className={`px-3 py-4 text-center ${
                        isCurrentPlan(plan) 
                          ? 'bg-gradient-to-r from-primary-50 to-primary-100 border-l-2 border-r-2 border-primary-300' 
                          : getBestChoicePackage()?.key === plan.key 
                            ? 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-100 border-l-2 border-r-2 border-amber-400' 
                            : ''
                      }`}>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm font-bold ${
                          shouldGrayOutTrial(plan) ? 'bg-gray-200 text-gray-400' : ''
                        }`}>
                          {plan.metadata?.devices === 'unlimited' || parseInt(plan.metadata?.devices) > 100 
                            ? '∞' 
                            : plan.metadata?.devices || '1'}
                        </span>
                      </td>
                    ))}
                  </tr>
                  
                  {/* Screen Creation Row */}
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 text-sm font-semibold text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100 border-r border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-indigo-100 rounded-lg">
                          <Monitor className="w-4 h-4 text-indigo-600" />
                        </div>
                        <span>Screens</span>
                      </div>
                    </td>
                    {subscriptions.map((plan) => (
                      <td key={plan.key} className={`px-3 py-4 text-center ${
                        isCurrentPlan(plan) 
                          ? 'bg-gradient-to-r from-primary-50 to-primary-100 border-l-2 border-r-2 border-primary-300' 
                          : getBestChoicePackage()?.key === plan.key 
                            ? 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-100 border-l-2 border-r-2 border-amber-400' 
                            : ''
                      }`}>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm font-bold ${
                          shouldGrayOutTrial(plan) ? 'bg-gray-200 text-gray-400' : ''
                        }`}>
                          {plan.metadata?.devices === 'unlimited' || parseInt(plan.metadata?.devices) > 100 
                            ? '∞' 
                            : (parseInt(plan.metadata?.devices) * 2) || '2'}
                        </span>
                      </td>
                    ))}
                  </tr>
                  
                  {/* Ad-Free Experience Row */}
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 text-sm font-semibold text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100 border-r border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-emerald-100 rounded-lg">
                          <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                        </div>
                        <span>Ad-Free</span>
                      </div>
                    </td>
                    {subscriptions.map((plan) => (
                      <td key={plan.key} className={`px-3 py-4 text-center ${
                        isCurrentPlan(plan) 
                          ? 'bg-gradient-to-r from-primary-50 to-primary-100 border-l-2 border-r-2 border-primary-300' 
                          : getBestChoicePackage()?.key === plan.key 
                            ? 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-100 border-l-2 border-r-2 border-amber-400' 
                            : ''
                      }`}>
                        {plan.isTrial ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                            <CheckCircle className="w-3 h-3" />
                            Trial
                          </span>
                        ) : (
                          <div className="inline-flex items-center justify-center w-6 h-6 bg-emerald-100 rounded-full">
                            <CheckCircle className="w-4 h-4 text-emerald-600" />
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                  
                  {/* AI-Powered Platform Row */}
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 text-sm font-semibold text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100 border-r border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-purple-100 rounded-lg">
                          <Zap className="w-4 h-4 text-purple-600" />
                        </div>
                        <span>AI Platform</span>
                      </div>
                    </td>
                    {subscriptions.map((plan) => (
                      <td key={plan.key} className={`px-3 py-4 text-center ${
                        isCurrentPlan(plan) 
                          ? 'bg-gradient-to-r from-primary-50 to-primary-100 border-l-2 border-r-2 border-primary-300' 
                          : getBestChoicePackage()?.key === plan.key 
                            ? 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-100 border-l-2 border-r-2 border-amber-400' 
                            : ''
                      }`}>
                        {plan.isTrial ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                            <CheckCircle className="w-3 h-3" />
                            Trial
                          </span>
                        ) : (
                          <div className="inline-flex items-center justify-center w-6 h-6 bg-purple-100 rounded-full">
                            <CheckCircle className="w-4 h-4 text-purple-600" />
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Video & Dynamic Content Row */}
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 text-sm font-semibold text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100 border-r border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-red-100 rounded-lg">
                          <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                        </div>
                        <span>Video Content</span>
                      </div>
                    </td>
                    {subscriptions.map((plan) => (
                      <td key={plan.key} className={`px-3 py-4 text-center ${
                        isCurrentPlan(plan) 
                          ? 'bg-gradient-to-r from-primary-50 to-primary-100 border-l-2 border-r-2 border-primary-300' 
                          : getBestChoicePackage()?.key === plan.key 
                            ? 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-100 border-l-2 border-r-2 border-amber-400' 
                            : ''
                      }`}>
                        {plan.isTrial ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                            <CheckCircle className="w-3 h-3" />
                            Trial
                          </span>
                        ) : (
                          <div className="inline-flex items-center justify-center w-6 h-6 bg-red-100 rounded-full">
                            <CheckCircle className="w-4 h-4 text-red-600" />
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Advanced Scheduling Row */}
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 text-sm font-semibold text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100 border-r border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-yellow-100 rounded-lg">
                          <Calendar className="w-4 h-4 text-yellow-600" />
                        </div>
                        <span>Scheduling</span>
                      </div>
                    </td>
                    {subscriptions.map((plan) => (
                      <td key={plan.key} className={`px-3 py-4 text-center ${
                        isCurrentPlan(plan) 
                          ? 'bg-gradient-to-r from-primary-50 to-primary-100 border-l-2 border-r-2 border-primary-300' 
                          : getBestChoicePackage()?.key === plan.key 
                            ? 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-100 border-l-2 border-r-2 border-amber-400' 
                            : ''
                      }`}>
                        {plan.isTrial ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                            <CheckCircle className="w-3 h-3" />
                            Trial
                          </span>
                        ) : (
                          <div className="inline-flex items-center justify-center w-6 h-6 bg-yellow-100 rounded-full">
                            <CheckCircle className="w-4 h-4 text-yellow-600" />
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Trial-specific rows */}
                  {subscriptions.some(plan => plan.isTrial) && (
                    <>
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4 text-sm font-semibold text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100 border-r border-gray-200">
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-blue-100 rounded-lg">
                              <Clock className="w-4 h-4 text-blue-600" />
                            </div>
                            <span>Trial Days</span>
                          </div>
                        </td>
                        {subscriptions.map((plan) => (
                          <td key={plan.key} className={`px-3 py-4 text-center ${
                            isCurrentPlan(plan) 
                              ? 'bg-gradient-to-r from-primary-50 to-primary-100 border-l-2 border-r-2 border-primary-300' 
                              : getBestChoicePackage()?.key === plan.key 
                                ? 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-100 border-l-2 border-r-2 border-amber-400' 
                                : ''
                          }`}>
                            {plan.isTrial ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                                {plan.metadata.days}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                      
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4 text-sm font-semibold text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100 border-r border-gray-200">
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-green-100 rounded-lg">
                              <CreditCard className="w-4 h-4 text-green-600" />
                            </div>
                            <span>No Credit Card</span>
                          </div>
                        </td>
                        {subscriptions.map((plan) => (
                          <td key={plan.key} className={`px-3 py-4 text-center ${
                            isCurrentPlan(plan) 
                              ? 'bg-gradient-to-r from-primary-50 to-primary-100 border-l-2 border-r-2 border-primary-300' 
                              : getBestChoicePackage()?.key === plan.key 
                                ? 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-100 border-l-2 border-r-2 border-amber-400' 
                                : ''
                          }`}>
                            {plan.isTrial ? (
                              <div className="inline-flex items-center justify-center w-6 h-6 bg-green-100 rounded-full">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    </>
                  )}
                  
                  {/* Action Buttons Row */}
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <td className="px-4 py-6 text-base font-bold text-gray-900 bg-white border-r border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg">
                          <Zap className="w-4 h-4 text-white" />
                        </div>
                        <span>Actions</span>
                      </div>
                    </td>
                    {subscriptions.map((plan) => (
                      <td key={plan.key} className={`px-3 py-6 text-center ${
                        isCurrentPlan(plan) 
                          ? 'bg-gradient-to-r from-primary-50 to-primary-100 border-l-2 border-r-2 border-primary-300' 
                          : getBestChoicePackage()?.key === plan.key 
                            ? 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-100 border-l-2 border-r-2 border-amber-400' 
                            : ''
                      }`}>
                        {isCurrentPlan(plan) ? (
                          (() => {
                            // Check if this plan is canceled but still inside its paid period
                            // (and not resubscribed). Once it expires isCurrentPlan() is false
                            // and the normal "Upgrade" button below takes over.
                            const canceledSub = userSubscriptions.find(sub =>
                              sub.product?.id === plan.key &&
                              sub.status === 'canceled' &&
                              SubscriptionService.isSubscriptionValid(sub)
                            );
                            const hasActiveSub = userSubscriptions.some(sub =>
                              sub.product?.id === plan.key && sub.status === 'active'
                            );

                            if (canceledSub && !hasActiveSub) {
                              // Canceled but still active, show disabled "Canceled" button
                              return (
                                <button
                                  disabled
                                  className="px-4 py-2 bg-orange-200 text-orange-700 rounded-lg font-semibold text-xs cursor-not-allowed shadow-sm"
                                >
                                  CANCELED
                                </button>
                              );
                            }

                            // Check if trial is canceled but still active
                            if (plan.isTrial && currentUserData?.trialData?.cancelled && 
                                currentUserData.trialData.endDate > new Date().getTime() && 
                                !currentUserData.trialData.expired) {
                              // Trial is canceled but still active, show "Trial Canceled" button
                              return (
                                <button
                                  disabled
                                  className="px-4 py-2 bg-orange-200 text-orange-700 rounded-lg font-semibold text-xs cursor-not-allowed shadow-sm"
                                >
                                  TRIAL CANCELED
                                </button>
                              );
                            }
                            
                            // Subscription is active, show cancel button
                            return (
                              <button
                                onClick={() => handleCancel(plan.key)}
                                className={`px-4 py-2 rounded-lg font-semibold text-xs shadow-sm transition-all hover:shadow-md ${
                                  plan.isTrial 
                                    ? 'text-blue-700 bg-blue-100 hover:bg-blue-200' 
                                    : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                                }`}
                              >
                                {plan.isTrial ? 'Cancel Trial' : 'Cancel Plan'}
                              </button>
                            );
                          })()
                        ) : (
                          <button
                            onClick={() => handleUpgrade(plan)}
                            disabled={plan.isTrial && (!canStartTrial(plan) || shouldGrayOutTrial(plan))}
                            className={`px-4 py-2 rounded-lg font-semibold text-xs shadow-sm transition-all hover:shadow-md ${
                              plan.isTrial 
                                ? shouldGrayOutTrial(plan)
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : canStartTrial(plan)
                                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700' 
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:from-primary-600 hover:to-primary-700'
                            }`}
                          >
                            {plan.isTrial 
                              ? shouldGrayOutTrial(plan)
                                ? 'Not Available'
                                : canStartTrial(plan) 
                                  ? 'Start Trial' 
                                  : 'Trial Used'
                              : 'Upgrade'
                            }
                          </button>
                        )}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        {/* Mobile-friendly alternative for small screens */}
        <div className="mt-6 lg:hidden">
          <p className="text-sm text-gray-500 text-center mb-4">
            💡 Swipe horizontally to see all plans on mobile
          </p>
        </div>
      </div>

      {/* Billing History */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Calendar className="w-5 h-5 text-blue-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Billing History</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-700">Date</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Description</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Amount</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {userSubscriptions.length > 0 ? (
                userSubscriptions.map((sub, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="py-3 px-4 text-sm text-gray-900">
                      {sub.current_period_start ? new Date(sub.current_period_start * 1000).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-700">
                      {subscriptions.find(s => s.key === sub.product?.id)?.name || 'Unknown Plan'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-900">
                      {(() => {
                        // Try different possible price paths
                        const price = sub.items?.data?.[0]?.price?.unit_amount || 
                                     sub.price?.unit_amount || 
                                     sub.amount || 
                                     sub.items?.data?.[0]?.amount ||
                                     0;
                        
                        // If we have a price, format it
                        if (price > 0) {
                          return `C$${(price / 100).toFixed(2)}`;
                        }
                        
                        // If no price found, try to get it from the subscription plan
                        const plan = subscriptions.find(s => s.key === sub.product?.id);
                        if (plan && plan.prices && plan.prices.length > 0) {
                          return `C$${(plan.prices[0].unit_amount / 100).toFixed(2)}`;
                        }
                        
                        return 'C$0.00';
                      })()}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                        sub.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        <CheckCircle className="w-3 h-3" />
                        {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="py-8 px-4 text-center text-gray-500">
                    No billing history available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upgrade Confirmation Modal */}
      {showUpgradeConfirm && selectedSubscription && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] overflow-hidden" style={{ margin: 0, padding: 0 }}>
          <div className="bg-white rounded-xl p-8 max-w-lg w-full mx-4 border border-primary-200" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-primary-100 rounded-full">
                <CreditCard className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Upgrade Subscription</h3>
                <p className="text-sm text-gray-500">You'll be redirected to complete your purchase</p>
              </div>
            </div>
            
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-primary-800 mb-2">Subscription Details:</h4>
              <ul className="text-sm text-primary-700 space-y-1">
                <li>• Plan: <strong>{selectedSubscription.name}</strong></li>
                <li>• Price: <strong>C${getPrice(selectedSubscription)}/{getInterval(selectedSubscription)}</strong></li>
                <li>• Billing: Automatic renewal</li>
                <li>• Cancel anytime from your account</li>
              </ul>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to upgrade to the <strong>{selectedSubscription.name}</strong> plan? 
              You'll be redirected to our secure payment processor to complete your subscription.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowUpgradeConfirm(false)
                  setSelectedSubscription(null)
                }}
                disabled={upgradeLoading}
                className="flex-1 px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmUpgrade}
                disabled={upgradeLoading}
                className="flex-1 px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {upgradeLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : (
                  'Continue to Payment'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trial Confirmation Modal */}
      {showTrialConfirm && selectedSubscription && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] overflow-hidden" style={{ margin: 0, padding: 0 }}>
          <div className="bg-white rounded-xl p-8 max-w-lg w-full mx-4 border border-blue-200" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-blue-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Start Free Trial</h3>
                <p className="text-sm text-gray-500">No credit card required</p>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-blue-800 mb-2">Trial Details:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Duration: <strong>{selectedSubscription.metadata.days} days</strong></li>
                <li>• Devices: <strong>Up to {selectedSubscription.metadata.devices}</strong></li>
                <li>• Screens: <strong>Up to {selectedSubscription.metadata.devices * 2}</strong></li>
                <li>• Features: All premium features included (including AI)</li>
                <li>• Ad-free experience</li>
                <li>• Video and dynamic content creation</li>
                <li>• No credit card required</li>
                <li>• Cancel anytime</li>
              </ul>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to start your free trial of <strong>{selectedSubscription.name}</strong>? 
              You'll have access to all premium features for {selectedSubscription.metadata.days} days with no obligation.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowTrialConfirm(false)
                  setSelectedSubscription(null)
                }}
                disabled={trialLoading}
                className="flex-1 px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmTrial}
                disabled={trialLoading}
                className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                {trialLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Starting Trial...
                  </>
                ) : (
                  'Start Free Trial'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Trial Confirmation Modal */}
      {showCancelTrialConfirm && selectedSubscription && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] overflow-hidden" style={{ margin: 0, padding: 0 }}>
          <div className={`bg-white rounded-xl p-8 max-w-lg w-full mx-4 border ${
            currentUserData?.trialData?.cancelled ? 'border-orange-200' : 'border-blue-200'
          }`} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-4 mb-6">
              <div className={`p-3 rounded-full ${
                currentUserData?.trialData?.cancelled ? 'bg-orange-100' : 'bg-blue-100'
              }`}>
                {currentUserData?.trialData?.cancelled ? (
                  <XCircle className="w-6 h-6 text-orange-600" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-blue-600" />
                )}
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {currentUserData?.trialData?.cancelled ? 'Trial Status' : 'Manage Trial'}
                </h3>
                <p className="text-sm text-gray-500">
                  {currentUserData?.trialData?.cancelled ? 'View your canceled trial status' : 'Keep or cancel your trial'}
                </p>
              </div>
            </div>
            
            <div className={`${
              currentUserData?.trialData?.cancelled ? 'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200'
            } border rounded-lg p-4 mb-6`}>
              <h4 className={`font-semibold mb-2 ${
                currentUserData?.trialData?.cancelled ? 'text-orange-800' : 'text-blue-800'
              }`}>Trial Status:</h4>
              <ul className={`text-sm space-y-1 ${
                currentUserData?.trialData?.cancelled ? 'text-orange-700' : 'text-blue-700'
              }`}>
                <li>• Trial End Date: <strong>{new Date(currentUserData?.trialData?.endDate).toLocaleDateString()}</strong></li>
                <li>• You will continue to have all trial features until the end date</li>
                <li>• No charges will be made</li>
                <li>• You can upgrade to a paid plan anytime</li>
                {currentUserData?.trialData?.cancelled && (
                  <li>• <strong>Status: Trial has been canceled</strong></li>
                )}
              </ul>
            </div>
            
            <p className="text-gray-600 mb-6">
              {currentUserData?.trialData?.cancelled ? (
                <>
                  Your trial has already been canceled and will continue until <strong>{new Date(currentUserData?.trialData?.endDate).toLocaleDateString()}</strong>. 
                  You still have access to all trial features until the end date. You can upgrade to a paid plan anytime to continue using premium features.
                </>
              ) : (
                <>
                  Your trial will continue until <strong>{new Date(currentUserData?.trialData?.endDate).toLocaleDateString()}</strong>. 
                  You can choose to keep your trial or cancel it now. If you cancel, you'll still have access until the end date, but we'll be notified of your cancellation.
                </>
              )}
            </p>
            
            <div className="flex gap-3">
              {!currentUserData?.trialData?.cancelled ? (
                <>
                  <button
                    onClick={() => {
                      setShowCancelTrialConfirm(false)
                      setSelectedSubscription(null)
                    }}
                    disabled={trialLoading}
                    className="flex-1 px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Keep Trial
                  </button>
                  <button
                    onClick={confirmCancelTrial}
                    disabled={trialLoading}
                    className="flex-1 px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {trialLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Canceling...
                      </>
                    ) : (
                      'Cancel Trial'
                    )}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => {
                    setShowCancelTrialConfirm(false)
                    setSelectedSubscription(null)
                  }}
                  className="flex-1 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
                >
                  Got it
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Purchase Success Modal */}
      {showPurchaseSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] overflow-hidden" style={{ margin: 0, padding: 0 }}>
          <div className="bg-white rounded-xl p-8 max-w-lg w-full mx-4 border border-green-200" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Purchase Successful!</h3>
                <p className="text-sm text-gray-500">Your subscription has been activated</p>
              </div>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-green-800 mb-2">What's Next:</h4>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• Your subscription is now active</li>
                <li>• You have access to all premium features</li>
                <li>• Your billing cycle has started</li>
                <li>• You can manage your subscription anytime</li>
              </ul>
            </div>
            
            <p className="text-gray-600 mb-6">
              Thank you for subscribing! Your account has been upgraded and you now have access to all premium features. 
              You can manage your subscription and billing information from this page.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPurchaseSuccess(false)
                }}
                className="flex-1 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Canceled Modal */}
      {showPurchaseCanceled && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] overflow-hidden" style={{ margin: 0, padding: 0 }}>
          <div className="bg-white rounded-xl p-8 max-w-lg w-full mx-4 border border-yellow-200" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-yellow-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Purchase Canceled</h3>
                <p className="text-sm text-gray-500">No charges were made</p>
              </div>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-yellow-800 mb-2">What happened:</h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Your purchase was canceled</li>
                <li>• No charges were made to your account</li>
                <li>• Your current plan remains unchanged</li>
                <li>• You can try again anytime</li>
              </ul>
            </div>
            
            <p className="text-gray-600 mb-6">
              Your purchase was canceled and no charges were made to your account. 
              Your current subscription plan remains unchanged. You can try upgrading again anytime.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPurchaseCanceled(false)
                }}
                className="flex-1 px-6 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Subscriptions 